import { useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone, TableWrap } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { countryName } from '../../lib/business.ts';
import { dateTime, day, money } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import type { IconName } from '../../components/Icon.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

interface Closure {
  id: string;
  location: string;
  day: string;
  count: number;
  sales: number;
  refunds: number;
  invoices: number;
  total: number;
  tax: number;
  grand_total: number;
  closed_at: string;
}
interface Place {
  location: { id: string; name: string; code: string };
  country: string;
  time_zone: string;
  today: string;
  system: { system: string; status: 'ready' | 'needs_spec' | 'none' };
  adapter: { country: string; name: string; settings: string | null; active: boolean } | null;
  entries: number;
  last_entry: string | null;
  last_closure: Closure | null;
  open_days: string[];
}
interface Overview {
  locations: Place[];
  entries: number;
}
interface Entry {
  id: string;
  seq: number;
  kind: 'sale' | 'refund' | 'invoice' | 'credit_note';
  /** Invoices: "INV 12/2026". */
  document: string | null;
  location: string;
  receipt_no: number | null;
  at: string;
  day: string;
  total: number;
  tax: number;
  hash: string;
}
interface Check {
  ok: boolean;
  checked: number;
  problem: { seq: number; reason: 'missing' | 'broken_chain' | 'changed' } | null;
}

/** "2026-10-28" as a Date-free string compare: the days before today that can close. */
const closable = (p: Place) => p.open_days.filter((d) => d < p.today);

const SYSTEMS = ['al_cis', 'it_rt_sdi', 'de_tse', 'fr_nf525', 'es_verifactu'] as const;
/** The name of a country's tax office system, as people there know it. */
const systemName = (code: string) => ((SYSTEMS as readonly string[]).includes(code) ? t(`fj.system_${code as (typeof SYSTEMS)[number]}`) : t('fj.system_other'));

/** How each location's receipts reach (or will reach) its country's tax office. */
function systemLine(p: Place): { tone: Tone; icon: IconName; label: string; body: string } {
  const country = countryName(p.country);
  if (p.adapter?.active) return { tone: 'ok', icon: 'shieldCheck', label: t('fj.sys_on'), body: t('fj.sys_on_body', { name: p.adapter.name }) };
  if (p.adapter) return { tone: 'warn', icon: 'alert', label: t('fj.sys_off'), body: t('fj.sys_off_body', { name: p.adapter.name }) };
  if (p.system.status === 'ready') return { tone: 'warn', icon: 'alert', label: t('fj.sys_off'), body: t('fj.sys_off_body', { name: systemName(p.system.system) }) };
  if (p.system.status === 'none') return { tone: 'neutral', icon: 'info', label: t('fj.sys_none'), body: t('fj.sys_none_body', { country }) };
  return { tone: 'info', icon: 'lock', label: t('fj.sys_spec'), body: t('fj.sys_spec_body', { country, system: systemName(p.system.system) }) };
}

/**
 * Manage → Fiscal journal: every paid receipt and refund, numbered without gaps and sealed in a
 * chain that cannot be changed. Each location closes its days (a Z report with a grand total
 * that never resets). The page says, per location, how its country's tax office is served.
 */
export function FiscalJournal() {
  const { can } = useSession();
  const { data, reload } = useAction<Overview>('fiscal.overview');
  const [check, setCheck] = useState<Check | null>(null);
  const [checking, setChecking] = useState(false);
  const [closing, setClosing] = useState<{ place: Place; day: string } | null>(null);
  const [exporting, setExporting] = useState(false);
  const { busy, run } = useSave();

  const runCheck = async () => {
    setChecking(true);
    try {
      setCheck(await action<Check>('fiscal.verify', {}));
    } finally {
      setChecking(false);
    }
  };

  return (
    <ManageLayout
      title={t('manage.fiscal_journal')}
      subtitle={t('fj.subtitle')}
      actions={
        <>
          <Button icon="download" onClick={() => setExporting(true)} disabled={!data?.entries}>
            {t('fj.export')}
          </Button>
          <Button variant="primary" icon="shieldCheck" busy={checking} onClick={() => void runCheck()} disabled={!data}>
            {t('fj.check')}
          </Button>
        </>
      }
    >
      {check ? (
        <div className={`callout ${check.ok ? 'callout-ok' : 'callout-danger'}`} role="status">
          <Icon name={check.ok ? 'checkCircle' : 'alert'} size={18} />
          <span>
            {check.ok
              ? t('fj.check_ok', { n: check.checked })
              : t(`fj.check_${check.problem!.reason}`, { seq: check.problem!.seq })}
          </span>
        </div>
      ) : null}

      {!data ? (
        <Skeleton h={260} r="var(--radius-lg)" />
      ) : (
        <>
          <div className="fj-places">
            {data.locations.map((p) => {
              const sys = systemLine(p);
              const due = closable(p);
              return (
                <section key={p.location.id} className="fj-place" aria-label={p.location.name}>
                  <header className="fj-place-head">
                    <span className="place-badge" aria-hidden="true">
                      {p.location.code}
                    </span>
                    <div>
                      <h2>{p.location.name}</h2>
                      <span className="muted">
                        <Icon name="globe" size={14} /> {countryName(p.country)} · {p.time_zone}
                      </span>
                    </div>
                    <StatusBadge tone={sys.tone} icon={sys.icon} label={sys.label} />
                  </header>
                  <p className="fj-sys muted">
                    {sys.body}{' '}
                    {p.adapter?.settings && can('fiscal.manage') ? <Link to={p.adapter.settings}>{t('fj.open_settings')}</Link> : null}
                  </p>
                  <dl className="fj-stats">
                    <div>
                      <dt>{t('fj.receipts')}</dt>
                      <dd className="tabular">{p.entries}</dd>
                    </div>
                    <div>
                      <dt>{t('fj.last_closed')}</dt>
                      <dd>{p.last_closure ? day(p.last_closure.day) : '—'}</dd>
                    </div>
                    <div>
                      <dt>{t('fj.grand_total')}</dt>
                      <dd className="tabular">{p.last_closure ? money(p.last_closure.grand_total) : '—'}</dd>
                    </div>
                  </dl>
                  {due.length ? (
                    <div className="fj-due">
                      <span>
                        <Icon name="calendar" size={16} /> {t('fj.days_open', { n: due.length })}
                      </span>
                      {can('fiscal.close') ? (
                        <Button size="sm" variant="primary" icon="lock" onClick={() => setClosing({ place: p, day: due[0]! })}>
                          {t('fj.close_day', { day: day(due[0]!) })}
                        </Button>
                      ) : null}
                    </div>
                  ) : p.open_days.includes(p.today) ? (
                    <p className="muted fj-note">{t('fj.today_later')}</p>
                  ) : null}
                </section>
              );
            })}
          </div>

          <Closures places={data.locations} reloadKey={data} />
          <Journal places={data.locations} reloadKey={data} />

          <p className="muted fx-note">
            <Icon name="lock" size={14} /> {t('fj.how')}
          </p>
        </>
      )}

      {closing ? (
        <Confirm
          title={t('fj.close_title', { day: day(closing.day), place: closing.place.location.name })}
          body={t('fj.close_body')}
          confirm={t('fj.close_confirm')}
          busy={busy}
          onClose={() => setClosing(null)}
          onConfirm={() =>
            void run(async () => {
              await action('fiscal.close_day', { location: closing.place.location.id, day: closing.day });
              setClosing(null);
              await reload();
            }, t('fj.closed_done', { day: day(closing.day) }))
          }
        />
      ) : null}
      {exporting && data ? <ExportDialog places={data.locations} onClose={() => setExporting(false)} /> : null}
    </ManageLayout>
  );
}

function Closures({ places, reloadKey }: { places: Place[]; reloadKey: unknown }) {
  const [at, setAt] = useState(places[0]?.location.id ?? '');
  const { data } = useAction<Closure[]>('fiscal.closures', { location: at, _r: reloadKey ? places.map((p) => p.last_closure?.id).join() : '' });
  return (
    <section className="fj-section">
      <div className="fj-section-head">
        <h2 className="section-title">{t('fj.closures')}</h2>
        {places.length > 1 ? (
          <div className="chips" role="group" aria-label={t('fj.location')}>
            {places.map((p) => (
              <button key={p.location.id} className={`chip${at === p.location.id ? ' active' : ''}`} aria-pressed={at === p.location.id} onClick={() => setAt(p.location.id)}>
                {p.location.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={120} />
      ) : !data.length ? (
        <EmptyState icon="calendar" title={t('fj.no_closures')} body={t('fj.no_closures_body')} />
      ) : (
        <TableWrap>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('fj.day')}</th>
                <th>{t('fj.receipts')}</th>
                <th>{t('fj.sales')}</th>
                <th>{t('fj.refunds')}</th>
                {data.some((c) => c.invoices) ? <th>{t('fj.invoices')}</th> : null}
                <th>{t('fj.tax')}</th>
                <th>{t('fj.total')}</th>
                <th>{t('fj.grand_total')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id}>
                  <td>{day(c.day)}</td>
                  <td className="tabular">{c.count}</td>
                  <td className="tabular">{money(c.sales)}</td>
                  <td className="tabular">{c.refunds ? money(c.refunds) : '—'}</td>
                  {data.some((x) => x.invoices) ? <td className="tabular">{c.invoices ? money(c.invoices) : '—'}</td> : null}
                  <td className="tabular">{money(c.tax)}</td>
                  <td className="tabular strong">{money(c.total)}</td>
                  <td className="tabular">{money(c.grand_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}

/** What an entry is, as people say it: "Receipt 12", "Invoice 4/2026". */
function documentOf(e: Entry): string {
  const n = e.receipt_no ?? '—';
  if (e.kind === 'invoice') return t('fj.invoice_no', { n: e.document?.replace(/^INV /, '') ?? '—' });
  if (e.kind === 'credit_note') return t('fj.credit_no', { n: e.document?.replace(/^CN /, '') ?? '—' });
  return e.kind === 'refund' ? t('fj.refund_no', { n }) : t('fj.receipt_no', { n });
}

function Journal({ places, reloadKey }: { places: Place[]; reloadKey: unknown }) {
  const [more, setMore] = useState<Entry[]>([]);
  const [hasMore, setHasMore] = useState<boolean | null>(null);
  const { data } = useAction<{ entries: Entry[]; more: boolean }>('fiscal.journal', { limit: 25, _r: reloadKey ? places.length : 0 });
  const rows = [...(data?.entries ?? []), ...more];
  const name = (id: string) => places.find((p) => p.location.id === id)?.location.code ?? '—';
  const loadMore = async () => {
    const r = await action<{ entries: Entry[]; more: boolean }>('fiscal.journal', { limit: 50, before_seq: rows.at(-1)!.seq });
    setMore((m) => [...m, ...r.entries]);
    setHasMore(r.more);
  };
  return (
    <section className="fj-section">
      <h2 className="section-title">{t('fj.journal')}</h2>
      {!data ? (
        <Skeleton h={160} />
      ) : !rows.length ? (
        <EmptyState icon="receipt" title={t('fj.empty')} body={t('fj.empty_body')} />
      ) : (
        <>
          <TableWrap>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('fj.seq')}</th>
                  <th className="left">{t('fj.when')}</th>
                  <th className="left">{t('fj.location')}</th>
                  <th className="left">{t('fj.document')}</th>
                  <th>{t('fj.total')}</th>
                  <th className="left fj-seal">{t('fj.seal')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id}>
                    <td className="tabular mono">{e.seq}</td>
                    <td className="left fj-when">{dateTime(e.at)}</td>
                    <td className="left">
                      <span className="mono">{name(e.location)}</span>
                    </td>
                    <td className="left">
                      {documentOf(e)}
                    </td>
                    <td className="tabular">{money(e.total)}</td>
                    <td className="left mono muted fj-seal" title={e.hash}>
                      {e.hash.slice(0, 10)}…
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
          {(hasMore ?? data.more) ? (
            <Button variant="ghost" icon="down" onClick={() => void loadMore()}>
              {t('fj.load_more')}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

function ExportDialog({ places, onClose }: { places: Place[]; onClose: () => void }) {
  const today = places[0]?.today ?? new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const [at, setAt] = useState('');
  const { busy, run } = useSave();
  const bad = !from || !to || to < from;
  const go = () =>
    void run(async () => {
      const r = await action('fiscal.export', { from, to, location: at || null });
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `fiscal-journal-${from}-${to}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      onClose();
    }, t('fj.exported'));
  return (
    <Dialog
      title={t('fj.export_title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="download" busy={busy} disabled={bad} onClick={go}>
            {t('fj.export')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('fj.from')}>
          <input className="input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('fj.to')} error={to < from ? t('fj.to_before') : null}>
          <input className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </Field>
        {places.length > 1 ? (
          <Field label={t('fj.location')}>
            <select className="input" value={at} onChange={(e) => setAt(e.target.value)}>
              <option value="">{t('fj.all_locations')}</option>
              {places.map((p) => (
                <option key={p.location.id} value={p.location.id}>
                  {p.location.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <p className="muted form-wide">{t('fj.export_hint')}</p>
      </div>
    </Dialog>
  );
}
